// Setup Wizard (SCR-STAFF-900، AP-PLT-04): کلیدهای Cache و Hookهای وضعیت راه‌اندازی.
import { unwrap, type OnboardingStepState, type OnboardingView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import type { StatusTone } from '@phoenix/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export const SCR_STAFF_900 = 'SCR-STAFF-900';
export const SETUP_PATH = '/setup';
export const ONBOARDING_PERMISSION = 'plt.onboarding.manage';

export const onboardingKeys = {
  view: (tenantId: string | undefined) => ['platform', tenantId, 'onboarding'] as const,
};

/**
 * وضعیت Wizard مستأجر جاری؛ فقط با plt.onboarding.manage. `refetchInterval`: صفحه Wizard وضعیت را دوره‌ای هم می‌خواند تا کارهای
 * پس‌زمینه (اجرای ورود داده) بدون Mutation تازه در Stepper دیده شوند.
 */
export function useOnboarding(options: { refetchInterval?: number } = {}) {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const allowed = !access.isLoading && access.can(ONBOARDING_PERMISSION);
  const query = useQuery({
    queryKey: onboardingKeys.view(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/onboarding')),
    enabled: allowed,
    refetchInterval: options.refetchInterval,
  });
  return { ...query, allowed };
}

/**
 * وضعیت هر مرحله از داده واقعی ماژول‌ها محاسبه می‌شود؛ پس از هر تغییر موفق در فرم‌های جاسازی‌شده (Mutation) وضعیت Wizard دوباره
 * خوانده می‌شود.
 */
export function useRefreshOnboardingOnMutations(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'success') {
          void queryClient.invalidateQueries({ queryKey: onboardingKeys.view(tenantId) });
        }
      }),
    [queryClient, tenantId],
  );
}

export const STEP_TONE: Record<OnboardingStepState, StatusTone> = {
  Complete: 'done',
  Incomplete: 'inactive',
  Skipped: 'pending',
  ComingSoon: 'cancelled',
  FeatureDisabled: 'cancelled',
};

/** مراحل الزامی ناتمام. */
export function incompleteRequired(view: OnboardingView | undefined): string[] {
  return (view?.steps ?? [])
    .filter((step) => step.isAvailable && step.isRequired && !step.isComplete)
    .map((step) => step.key);
}
